import React from 'react';
import { DragDropList } from './DragDropList';
import type { ProgramExercise } from '../types';

interface ExerciseOrderManagerProps {
  exercises: ProgramExercise[];
  onReorder: (reorderedExercises: ProgramExercise[]) => void;
  onRemoveExercise?: (exerciseId: string) => void;
  onUpdateTargets?: (
    exerciseId: string,
    targetSets?: number,
    targetRepsMin?: number,
    targetRepsMax?: number,
    restSeconds?: number
  ) => void;
  className?: string;
}

/**
 * ExerciseOrderManager - Drag-and-drop exercise ordering component
 * Allows reordering exercises within a workout with visual feedback
 * Requirements: 3.4, 5.2
 */
export const ExerciseOrderManager: React.FC<ExerciseOrderManagerProps> = ({
  exercises,
  onReorder,
  onRemoveExercise,
  onUpdateTargets,
  className = ''
}) => {
  const handleReorder = (reorderedExercises: ProgramExercise[]) => {
    // Update order_index for each exercise
    const exercisesWithNewOrder = reorderedExercises.map((exercise, index) => ({
      ...exercise,
      order_index: index
    }));
    
    onReorder(exercisesWithNewOrder);
  };

  const renderExerciseItem = (exercise: ProgramExercise, index: number, isDragging: boolean) => (
    <div className={`
      border border-gray-200 rounded-lg p-4 bg-white
      ${isDragging ? 'shadow-lg border-blue-300' : 'hover:border-gray-300'}
    `}>
      {/* Drag Handle and Exercise Info */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start space-x-3 flex-1">
          {/* Drag Handle */}
          <div className="drag-handle mt-1 cursor-move text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </div>
          
          {/* Exercise Details */}
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                {index + 1}
              </span>
              <h4 className="font-medium text-gray-900">{exercise.exercise.name_ru}</h4>
            </div>
            <p className="text-sm text-gray-600">{exercise.exercise.muscle_group}</p>
            {exercise.exercise.equipment && (
              <p className="text-xs text-gray-500 mt-1">
                Оборудование: {exercise.exercise.equipment}
              </p>
            )}
          </div>
        </div>

        {/* Remove Button */}
        {onRemoveExercise && (
          <button
            onClick={() => onRemoveExercise(exercise.id)}
            className="text-red-600 hover:text-red-800 p-1 ml-2"
            title="Удалить упражнение"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {/* Target Sets and Reps */}
      {onUpdateTargets && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Подходы
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={exercise.target_sets || ''}
              onChange={(e) => onUpdateTargets(
                exercise.id,
                parseInt(e.target.value) || undefined,
                exercise.target_reps_min,
                exercise.target_reps_max
              )}
              className="block w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="3"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Повторы от
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={exercise.target_reps_min || ''}
              onChange={(e) => onUpdateTargets(
                exercise.id,
                exercise.target_sets,
                parseInt(e.target.value) || undefined,
                exercise.target_reps_max,
                exercise.rest_seconds
              )}
              className="block w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="8"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Повторы до
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={exercise.target_reps_max || ''}
              onChange={(e) => onUpdateTargets(
                exercise.id,
                exercise.target_sets,
                exercise.target_reps_min,
                parseInt(e.target.value) || undefined,
                exercise.rest_seconds
              )}
              className="block w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="12"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Отдых (сек)
            </label>
            <input
              type="number"
              min="0"
              max="600"
              step="15"
              value={exercise.rest_seconds || ''}
              onChange={(e) => onUpdateTargets(
                exercise.id,
                exercise.target_sets,
                exercise.target_reps_min,
                exercise.target_reps_max,
                parseInt(e.target.value) || undefined
              )}
              className="block w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="60"
            />
            <div className="text-xs text-gray-500 mt-1">
              {exercise.rest_seconds ? `${Math.floor(exercise.rest_seconds / 60)}:${(exercise.rest_seconds % 60).toString().padStart(2, '0')}` : '1:00'}
            </div>
          </div>
        </div>
      )}

      {/* Display current targets if not editable */}
      {!onUpdateTargets && (exercise.target_sets || exercise.target_reps_min || exercise.target_reps_max || exercise.rest_seconds) && (
        <div className="mt-3 text-sm text-gray-600">
          <span className="font-medium">Цель:</span>
          {exercise.target_sets && <span className="ml-1">{exercise.target_sets} подходов</span>}
          {(exercise.target_reps_min || exercise.target_reps_max) && (
            <span className="ml-1">
              × {exercise.target_reps_min || '?'}-{exercise.target_reps_max || '?'} повторов
            </span>
          )}
          {exercise.rest_seconds && (
            <span className="ml-1">
              • отдых {Math.floor(exercise.rest_seconds / 60)}:{(exercise.rest_seconds % 60).toString().padStart(2, '0')}
            </span>
          )}
        </div>
      )}
    </div>
  );

  if (exercises.length === 0) {
    return (
      <div className={`exercise-order-manager ${className}`}>
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-2">💪</div>
          <p>Упражнения не добавлены</p>
          <p className="text-sm">Добавьте упражнения, чтобы настроить их порядок</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`exercise-order-manager ${className}`}>
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Упражнения ({exercises.length})
          </h3>
          <div className="text-sm text-gray-500">
            Перетащите для изменения порядка
          </div>
        </div>
      </div>

      <DragDropList
        items={exercises}
        onReorder={handleReorder}
        renderItem={renderExerciseItem}
        className="space-y-3"
        itemClassName="transition-all duration-150"
      />

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Как изменить порядок упражнений:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Нажмите и удерживайте упражнение</li>
              <li>Перетащите его в нужное место</li>
              <li>Отпустите, чтобы зафиксировать новый порядок</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseOrderManager;